import React, { useState } from 'react';
import { useLibraryItemDetails } from './hooks/useLibraryItemDetails';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Input,
  Modal,
  Tooltip,
  Typography,
} from '@mui/material';
import styles from './LibraryItemDetails.module.scss';
import { ROUTES } from '../../common/constants/routes';
import { Link, useLocation, useParams } from 'react-router-dom';
import BrushIcon from '../../../assets/svg/brush.svg?react';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import ContractGenerator from '../../common/contract-generator/ContractGenerator';
import { useTranslation } from 'react-i18next';
import FileTypeDownloadModal from '../../common/file-type-download-modal/FileTypeDownloadModal';
import { useFileTypeDownload } from '../../common/file-type-download-modal/useFileTypeDownload';
import FileSaveIcon from '../../../assets/svg/file_save.svg?react';
import { LibraryItem } from '../library/components/library-list/types';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllDocuments } from '../../slices/documents/documentsSlice';
import { useLibraryFunctions } from '../library/hooks/useLibraryFunctions';
import { useDeleteLibraryItemMessageModal } from '../library/hooks/useDeleteMessageModal';
import MessageModal from '../../common/message-modal/MessageModal';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DisclaimerText from '../../common/disclaimer-text/DisclaimerText';
import { acceptDisclaimer, selectDisclaimerChecked } from '../../slices/disclaimer/disclaimerSlice';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import ContentCopyIcon from '../../../assets/svg/content_copy.svg?react';
import { selectUser } from '../../slices/user/userSlice';
import { Helpers } from '../../common/helpers/helpers';
import { toast } from 'react-toastify';
import { showMessage } from '../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import LibraryItemDetailsBlock from './components/library-item-details/LibraryItemDetailsBlock';
import { USER_PERMISSIONS } from '../../common/constants/permissions';
import { useCheckPermission } from '../../common/hooks/useCheckPermission';
import { Editor } from '@tinymce/tinymce-react';

const LibraryItemDetails = () => {
  const [copyModalOpen, setCopyModalOpen] = useState<LibraryItem | null>(null);
  const { name } = useParams();
  const documentUploads = useAppSelector(selectAllDocuments);
  const {
    dlHTML,
    setDLHTML,
    templateTitle,
    editMode,
    setEditMode,
    loadingHTML,
    contractCreationEnabled,
    setContractCreationEnabled,
    templateCategory,
    templateItem,
    handleSave,
    loading: savingTemplate,
    documentTypeLabel,
    setDocumentTypeLabel,
    setDocumentWorkflowLabel,
    documentWorkflowLabel,
    propertiesEditMode,
    setPropertiesEditMode,
    category,
    setCategory,
    categorySelectValues,
    getTypeLabels,
    setTemplateName,
    templateName,
    customLabel,
    setCustomLabel,
    workflowsArray,
    updateLibraryItem,
  } = useLibraryItemDetails();
  const {
    setModalOpen: setFileTypeModalOpen,
    modalOpen: fileTypeModalOpen,
    handleCloseModal: handleCloseFileTypeModal,
    libraryItem: libraryItemForDownloading,
    setLibraryItem: setLibraryItemForDownloading,
    setFileName: setFileNameForDownloading,
    fileName: fileNameForDownloading,
    fileType: fileTypeForDownloading,
    setFileType: setFileTypeForDownloading,
    handleTemplateDownload,
    pendingStatus: downloadPendingStatus,
  } = useFileTypeDownload();
  const {
    handleS3DownloadLink,
    disclaimerMessage,
    setDisclaimerMessage,
    handleLibraryItemCopy,
    handleTemplateCopy,
  } = useLibraryFunctions();
  const {
    modalOpen: deleteLibraryItemModalOpen,
    setModalOpen: setDeleteLibraryItemModalOpen,
    handleDeleteDocumentTemplate,
    libraryItemToDelete,
    setLibraryItemToDelete,
    pendingStatus: deletingLibraryItemPending,
  } = useDeleteLibraryItemMessageModal();
  const currentUser = useAppSelector(selectUser);
  const { t } = useTranslation();
  const checkPermission = useCheckPermission();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectAllCompanies)[0];
  const disclaimerAccepted = useAppSelector(selectDisclaimerChecked);
  const disclaimerStatus = useAppSelector((state) => state.disclaimerInfo.status);
  const freePlan = !company?.stripe_product_id && !company?.plan_settings.stripe_product_id;
  const actionAvailableFree = templateItem && 'is_free' in templateItem && templateItem?.is_free;
  const templateLabels =
    templateItem && 'region' in templateItem
      ? {
          categoryLabel: templateItem.category,
          typeLabel: templateItem.dl_types[0],
          workflowLabel: templateItem.dl_workflows[0],
        }
      : null;

  const CopyModal: React.FC<{ item: LibraryItem }> = ({ item }) => {
    const handleCopying = (newname: string) => {
      const data = {
        name: newname,
        category: item.category,
        dt_workflows: item.dl_workflows,
        dt_types: item.dl_types,
        user: currentUser.id,
      };

      try {
        handleLibraryItemCopy(data, item.id); // Wait for the copy operation to complete
        setCopyModalOpen(null); // Close the modal after the operation finishes
      } catch (error) {
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          dispatch(showMessage(t('messages.monthLimitReached')));
        } else {
          const errorMessage = Helpers.returnErrorText(error);
          toast.error(errorMessage.toString());
        }
      }
    };
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const newTemplateName = formData.get('newTemplateName') as string;
      if ('region' in item) {
        handleCopying(newTemplateName);
      } else {
        const updatedItem = { ...item, name: newTemplateName };
        try {
          handleTemplateCopy(updatedItem, currentUser.id);
          setCopyModalOpen(null);
        } catch (error) {
          if (
            error.response?.data?.errors?.length > 0 &&
            error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
          ) {
            dispatch(showMessage(t('messages.monthLimitReached')));
          } else {
            const errorMessage = Helpers.returnErrorText(error);
            toast.error(errorMessage.toString());
          }
        }
      }
    };
    return (
      <Modal
        open={copyModalOpen?.id === item.id}
        className={styles.copymodal}
        onClose={() => setCopyModalOpen(null)}
      >
        <Grid container sx={{ boxShadow: 'none', border: 'none', outline: 'none' }}>
          <Grid size={{ xs: 12, sm: 11, md: 11, lg: 11 }}>
            <Card>
              <CardContent sx={{ paddingBottom: '16px !important' }}>
                <Typography variant={'h6'} gutterBottom>
                  Copy to Own library
                </Typography>

                <Typography>Please change the template name if necessary.</Typography>
                <form onSubmit={handleSubmit}>
                  <Box sx={{ maxHeight: '500px', overflow: 'auto' }}>
                    <Input
                      name='newTemplateName'
                      placeholder={item.name ? item.name : t('templatePage.headerDefault')}
                      defaultValue={`${
                        item.name ? item.name : t('templatePage.headerDefault')
                      } - ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`}
                      className={styles.input}
                      fullWidth
                      required
                    />
                  </Box>

                  <Grid container justifyContent='flex-end' gap={'8px'}>
                    <Button
                      variant='contained'
                      onClick={() => setCopyModalOpen(null)}
                      className={'btn-default btn-action-basic btn-cancel'}
                    >
                      Go back
                    </Button>

                    <Button
                      variant='contained'
                      type='submit'
                      className='btn-default btn-action-basic'
                    >
                      Confirm
                    </Button>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Modal>
    );
  };

  const handleGenerateContract = () => {
    const actionAvailableFree = 'is_free' in templateItem && templateItem.is_free;
    if ((freePlan && actionAvailableFree) || !freePlan || !('region' in templateItem)) {
      if (disclaimerAccepted) {
        setContractCreationEnabled(true);
      } else {
        setDisclaimerMessage(true);
      }
    } else if (freePlan && !actionAvailableFree) {
      dispatch(showMessage(t('messages.')));
    }
  };
  const handleDownloading = (item: LibraryItem) => {
    if (!item.download_link) {
      setFileNameForDownloading(
        'name' in item
          ? item.name
          : documentUploads
              ?.find((el) => el.id === item.document_upload)
              ?.name?.replace('.pdf', '')
              .replace('.docx', '')
              .replace('.doc', '')
      );
      setLibraryItemForDownloading(item);
      setFileTypeModalOpen(true);
    } else {
      handleS3DownloadLink(item.id, 'name' in item ? item.name : '');
    }
  };
  return (
    <>
      <Box>
        <Grid container className={styles.header}>
          <Grid size={{ xs: 10 }}>
            <Typography variant='h5' component='h1' className={styles.title}>
              {location.pathname.includes(ROUTES.DOCUMENT_LIBRARY)
                ? t('headers.documentTemplates')
                : t('headers.workspace')}
            </Typography>
            <Box className={styles.breadcrumbs}>
              <Link
                to={
                  ROUTES.DOCUMENT_LIBRARY +
                  (name === 'uk' || name === 'au' || name === 'global' ? '' : '?libraryType=own')
                }
                className={styles.documentViewText}
              >
                {name === 'uk' || name === 'au' || name === 'global'
                  ? t('buttons.businessKitzLibrary')
                  : t('buttons.ownLibrary')}
              </Link>
              <Typography variant='body2' className={styles.separator}>
                ›
              </Typography>
              {templateItem?.category && (
                <>
                  <Link
                    to={`${ROUTES.DOCUMENT_LIBRARY}?category=${templateItem?.category}${
                      name === 'uk' || name === 'au' || name === 'global' ? '' : '&libraryType=own'
                    }`}
                    className={styles.documentViewText}
                  >
                    {templateItem?.category
                      ? Helpers.capitalizeFirstLetter(templateItem.category)
                      : ''}
                  </Link>
                  <Typography variant='body2' className={styles.separator}>
                    ›
                  </Typography>
                  {templateItem?.dl_workflows && templateItem?.dl_workflows.length > 0 && (
                    <>
                      <Link
                        to={`${ROUTES.DOCUMENT_LIBRARY}?category=${templateItem?.category}&workflow=${templateItem?.dl_workflows[0]}`}
                        className={styles.documentViewText}
                      >
                        {templateItem?.dl_workflows.length > 0
                          ? Helpers.capitalizeFirstLetter(templateItem?.dl_workflows[0])
                          : ''}
                      </Link>
                      <Typography variant='body2' className={styles.separator}>
                        ›
                      </Typography>
                    </>
                  )}
                </>
              )}
              <Typography variant='body2' className={styles.documentViewText}>
                {templateTitle}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid
          container
          justifyContent='space-between'
          spacing={2}
          className='inputs-editable'
          mt='-34px'
        >
          <Grid
            size={{ xs: 12 }}
            className={styles.gridItem}
            sx={{ position: 'relative', paddingRight: '16px', paddingBottom: '0' }}
          >
            <Box
              display='flex'
              paddingBottom='16px'
              paddingRight='8px'
              justifyContent='space-between'
              alignItems='center'
            >
              <Box>
                <Grid container className='mb-1 pt-0 pb-0 ps-0 pe-0'>
                  <Typography variant='h2' className={styles.title}>
                    {templateTitle}
                  </Typography>
                </Grid>
                {templateLabels && (
                  <Grid container className={styles.labels_row}>
                    {templateLabels && templateLabels.categoryLabel
                      ? templateLabels.categoryLabel
                      : null}
                    {templateLabels && templateLabels.workflowLabel
                      ? ' / ' + templateLabels.workflowLabel
                      : null}
                    {templateLabels && templateLabels.typeLabel
                      ? ' / ' + templateLabels.typeLabel
                      : null}
                  </Grid>
                )}
              </Box>

              <Box display='flex' alignItems='flex-start'>
                {checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_CONTRACT_CREATE) ? (
                  <Button
                    style={{ marginRight: '8px', flexShrink: 0 }}
                    type='button'
                    className='btn-default btn-small btn-action-basic'
                    role='button'
                    variant='contained'
                    onClick={() => {
                      handleGenerateContract();
                    }}
                  >
                    {t('buttons.generateContract')}
                  </Button>
                ) : (
                  <Tooltip title={'messages.contactAdministrator'} arrow placement='top'>
                    <Button
                      style={{ marginRight: '8px', flexShrink: 0 }}
                      type='button'
                      className='btn-default btn-small btn-action-basic'
                      role='button'
                      variant='contained'
                      disabled={true}
                    >
                      {t('buttons.generateContract')}
                    </Button>
                  </Tooltip>
                )}
                {templateCategory === 'company-templates' ? (
                  editMode ? (
                    <Button
                      style={{ marginRight: '8px' }}
                      type='button'
                      className='btn-default btn-small btn-action-basic'
                      role='button'
                      variant='contained'
                      onClick={() => {
                        handleSave();
                      }}
                    >
                      {t('buttons.save')}
                    </Button>
                  ) : (
                    <Tooltip
                      title={
                        checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_EDIT)
                          ? t('buttons.edit')
                          : t('messages.contactAdministrator')
                      }
                      placement='top'
                      arrow
                    >
                      <IconButton
                        size='small'
                        className={styles.editButton}
                        onClick={() => setEditMode(true)}
                        disabled={!checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_EDIT)}
                      >
                        <BrushIcon />
                      </IconButton>
                    </Tooltip>
                  )
                ) : null}
                <Tooltip
                  title={
                    (templateItem?.region &&
                      checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_LIBRARY_READ)) ||
                    (!templateItem?.region &&
                      checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_READ))
                      ? t('buttons.download')
                      : t('messages.contactAdministrator')
                  }
                  placement='top'
                  arrow
                >
                  <IconButton
                    size='small'
                    className={styles.actionButton}
                    onClick={() => handleDownloading(templateItem)}
                  >
                    <FileSaveIcon />
                  </IconButton>
                </Tooltip>
                {!templateItem?.download_link && (
                  <Tooltip
                    title={
                      checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_CREATE)
                        ? t('buttons.copyDocument')
                        : t('messages.contactAdministrator')
                    }
                    placement='top'
                    arrow
                  >
                    <IconButton
                      size='small'
                      disabled={!checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_CREATE)}
                      onClick={() => {
                        if (freePlan || !templateItem?.region) {
                          if (actionAvailableFree || !templateItem?.region) {
                            setCopyModalOpen(templateItem);
                          } else {
                            dispatch(showMessage(t('messages.considerStartingPlanLibrary')));
                          }
                        } else {
                          setCopyModalOpen(templateItem);
                        }
                      }}
                      className={styles.action_button}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {!templateItem?.region && (
                  <Tooltip
                    title={
                      checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_DELETE)
                        ? t('messages.delete')
                        : t('messages.contactAdministrator')
                    }
                    placement='top'
                    arrow
                  >
                    <IconButton
                      size='small'
                      aria-label='delete'
                      disabled={!checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_DELETE)}
                      onClick={() => {
                        setLibraryItemToDelete(templateItem);
                        setDeleteLibraryItemModalOpen(true);
                      }}
                    >
                      <DeleteOutlineOutlinedIcon
                        style={{ marginRight: 0, fill: '#152a58', width: '20px', height: '20px' }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
            <Box
              className={styles.tabContent}
              sx={{ height: `calc(100vh - 183px)`, overflow: loadingHTML ? 'hidden' : 'auto' }}
            >
              <Box className={styles.mainContent}>
                {contractCreationEnabled && (
                  <Box width='35%'>
                    <ContractGenerator
                      templateTitle={templateTitle}
                      libraryItem={templateItem}
                      templateId={templateItem?.id}
                      setContractCreationEnabled={setContractCreationEnabled}
                    />
                  </Box>
                )}
                {!contractCreationEnabled && templateItem && 'document_upload' in templateItem && (
                  <Box width='35%'>
                    <LibraryItemDetailsBlock
                      categoryArray={categorySelectValues}
                      workflowsArray={workflowsArray()}
                      typeLabels={getTypeLabels()}
                      propertiesEditMode={propertiesEditMode}
                      setPropertiesEditMode={setPropertiesEditMode}
                      documentTypeLabel={documentTypeLabel}
                      setDocumentTypeLabel={setDocumentTypeLabel}
                      documentWorkflowLabel={documentWorkflowLabel}
                      setDocumentWorkflowLabel={setDocumentWorkflowLabel}
                      templateName={templateName}
                      setTemplateName={setTemplateName}
                      category={category}
                      setCategory={setCategory}
                      updateLibraryItem={() => updateLibraryItem()}
                      customLabel={customLabel}
                      setCustomLabel={setCustomLabel}
                    />
                  </Box>
                )}
                <div
                  className={`${styles.main_container} ${
                    contractCreationEnabled || (templateItem && 'document_upload' in templateItem)
                      ? styles.w_55
                      : ''
                  }`}
                >
                  <div
                    id='editor'
                    className='main-editor-container template-editor'
                    style={{ height: `calc(100vh - 183px)` }}
                  >
                    {templateItem && (
                      <Editor
                        tinymceScriptSrc='/tinymce/tinymce.min.js'
                        licenseKey='gpl'
                        disabled={
                          templateItem && 'document_upload' in templateItem ? !editMode : true
                        }
                        init={{
                          content_style:
                            '#tinymce {max-width: 210mm; margin-left: auto; margin-right: auto; padding:2.54cm; box-shadow: 0 0 3px rgba(0, 0, 0, 0.3)} img {display: none !important;} tox-promotion {display: none !important;}',
                          menubar:
                            templateItem && 'document_upload' in templateItem
                              ? 'edit insert view format table tools'
                              : false,
                          plugins:
                            templateItem && 'document_upload' in templateItem
                              ? [
                                  // Core editing features
                                  'anchor',
                                  'autolink',
                                  'charmap',
                                  'codesample',
                                  'emoticons',
                                  'link',
                                  'lists',
                                  //'media',
                                  'searchreplace',
                                  'table',
                                  'visualblocks',
                                  'wordcount',
                                  // Your account includes a free trial of TinyMCE premium features
                                  // Try the most popular premium features until Jan 31, 2026:
                                  /*'checklist',
                                  'mediaembed',
                                  'casechange',
                                  'formatpainter',
                                  'pageembed',
                                  'a11ychecker',
                                  'tinymcespellchecker',
                                  'permanentpen',
                                  'powerpaste',
                                  'advtable',
                                  'advcode',
                                  'advtemplate',
                                  'ai',
                                  'uploadcare',
                                  //'mentions',
                                  //'tinycomments',
                                  'tableofcontents',
                                  //'footnotes',
                                  //'mergetags',
                                  'autocorrect',
                                  'typography',
                                  'inlinecss',
                                  'markdown',
                                  //'importword',
                                  //'exportword',
                                  //'exportpdf',*/
                                ]
                              : [],
                          toolbar: false,
                          /*'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link table | spellcheckdialog a11ycheck typography uploadcare | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat',*/
                          ai_request: (request: any, respondWith: any) =>
                            respondWith.string(() =>
                              Promise.reject('See docs to implement AI Assistant')
                            ),
                          uploadcare_public_key: 'b97265d9de273c24383a',
                          // XSS prevention: strip <script> tags and on* event attributes from pasted/loaded content
                          invalid_elements: 'script,object,embed,applet,iframe,form,input,button',
                          extended_valid_elements: '',
                          cleanup_on_startup: true,
                          convert_unsafe_embeds: true,
                          sandbox_iframes: true,
                          link_assume_external_targets: false,
                          allow_script_urls: false,
                        }}
                        value={dlHTML}
                        onEditorChange={(value) => {
                          if (value) {
                            setDLHTML(value);
                          } else {
                            setDLHTML('');
                          }
                        }}
                        //initialValue='Welcome to TinyMCE!'
                      />
                      /*<JoditEditor
                                                                                          ref={editor}
                                                                                          config={htmlEditorConfig}
                                                                                          value={dlHTML}
                                                                                          onBlur={(newContent) => setDLHTML(newContent)} // preferred to use only this option to update the content for performance reasons
                                                                                        />*/
                    )}
                  </div>
                </div>
              </Box>
            </Box>
            {(loadingHTML || disclaimerStatus === 'loading' || savingTemplate) && (
              <ProgressOverlay className={'position-absolute'} />
            )}
          </Grid>
        </Grid>
      </Box>
      {fileTypeModalOpen && libraryItemForDownloading && (
        <FileTypeDownloadModal
          onClose={() => {
            handleCloseFileTypeModal();
          }}
          open={fileTypeModalOpen}
          fileName={fileNameForDownloading}
          fileType={fileTypeForDownloading}
          handleFileTypeChange={setFileTypeForDownloading}
          handleFileNameChange={setFileNameForDownloading}
          libraryItem={libraryItemForDownloading}
          handleDownloadLibraryHTML={() => {
            handleTemplateDownload(
              libraryItemForDownloading.id,
              fileTypeForDownloading,
              fileNameForDownloading,
              'document_upload' in libraryItemForDownloading
            );
          }}
        />
      )}
      {deleteLibraryItemModalOpen && libraryItemToDelete && (
        <MessageModal
          open={deleteLibraryItemModalOpen}
          modalTitle={t('dialogTitles.deleteTemplate')}
          modalMessage={t('messages.deleteTemplate')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => {
            setLibraryItemToDelete(null);
            setDeleteLibraryItemModalOpen(false);
          }}
          goBackButtonText={t('buttons.delete')}
          document={libraryItemToDelete}
          onClose={() => {
            handleDeleteDocumentTemplate();
          }}
        />
      )}
      {disclaimerMessage && (
        <MessageModal
          open={disclaimerMessage}
          modalTitle={t('headers.documentTerms')}
          modalContent={<DisclaimerText />}
          actionButtonText={t('buttons.accept')}
          headerVariant={'h5'}
          actionButtonFn={() => {
            dispatch(acceptDisclaimer())
              .unwrap()
              .then(() => {
                setDisclaimerMessage(false);
                setContractCreationEnabled(true);
                //setGeneratorOpened(true);
              });
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setDisclaimerMessage(false);
          }}
        />
      )}
      {copyModalOpen && <CopyModal item={templateItem} />}
      {(downloadPendingStatus ||
        deletingLibraryItemPending ||
        disclaimerStatus === 'loadingAccepting') && <ProgressOverlay />}
    </>
  );
};

export default LibraryItemDetails;
